import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, Brain, Loader2, TrendingUp, TrendingDown, Minus, Anchor, Ship } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getAuthPhotoUrl } from '../components/AuthImage';

const SUPPORT_ICONS = {
  'VNM': Ship,
  'ENGINS TRACTÉS': Anchor,
  'ENGINS TRACTES': Anchor,
};

const BulletinNotes = ({ user }) => {
  const [searchParams] = useSearchParams();
  const stagiaireId = searchParams.get('id');
  const [bulletin, setBulletin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [synthese, setSynthese] = useState(null);
  const [syntheseLoading, setSyntheseLoading] = useState(false);

  useEffect(() => {
    if (stagiaireId) loadBulletin();
  }, [stagiaireId]);

  const loadBulletin = async () => {
    try {
      const res = await axios.get(`/bulletin/${stagiaireId}`);
      setBulletin(res.data);
    } catch {
      toast.error('Erreur chargement bulletin');
    } finally {
      setLoading(false);
    }
  };

  const generateSynthese = async () => {
    if (!bulletin) return;
    setSyntheseLoading(true);
    toast.info('Generation de la synthese IA en cours... (15-20 secondes)');
    try {
      const res = await axios.post(`/bulletin/${stagiaireId}/synthese-ia`, {
        supports: bulletin.supports,
        stagiaire_nom: `${bulletin.stagiaire.prenom} ${bulletin.stagiaire.nom}`
      }, { timeout: 60000 });
      setSynthese(res.data);
      toast.success('Synthese IA generee !');
      setTimeout(() => {
        const el = document.querySelector('[data-testid="synthese-globale"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur generation IA');
    } finally {
      setSyntheseLoading(false);
    }
  };

  const exportPDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const s = bulletin.stagiaire;
    const today = new Date().toLocaleDateString('fr-FR');
    let y = 10;

    // Header
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, 210, 35, 'F');
    doc.setFillColor(141, 198, 63);
    doc.rect(0, 35, 210, 3, 'F');
    doc.setTextColor(255);
    doc.setFontSize(18);
    doc.text('Bulletin de Notes', 14, 16);
    doc.setFontSize(10);
    doc.text(`${s.prenom} ${s.nom}${s.numero_candidat ? ` - N${s.numero_candidat}` : ''}`, 14, 24);
    doc.setFontSize(8);
    doc.text(`Centre: ${s.centre_formation || 'N/A'} | Date: ${today}`, 14, 30);
    doc.setFontSize(11);
    doc.text(`Moyenne Generale: ${bulletin.moyenne_generale}/10`, 150, 16);
    doc.text(`Moyenne Classe: ${bulletin.classe_generale}/10`, 150, 24);
    y = 42;

    // Supports
    for (const sup of bulletin.supports) {
      if (y > 260) { doc.addPage(); y = 15; }
      // Support header
      doc.setFillColor(30, 58, 95);
      doc.rect(10, y - 1, 190, 8, 'F');
      doc.setTextColor(255);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(`${sup.support}`, 14, y + 5);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      doc.text(`Moy: ${sup.moyenne}/10 | Classe: ${sup.class_avg}/10 | ${sup.total_notes} notes`, 130, y + 5);
      y += 11;

      // Categories table
      const body = [];
      for (const cat of sup.categories) {
        const notesStr = cat.notes.map(n => `${n.note}/10 (${(n.date || '').slice(5)})`).join(', ');
        const comments = cat.notes.filter(n => n.commentaire).map(n => `${n.formateur}: ${n.commentaire}`).join(' | ');
        body.push([
          cat.categorie,
          notesStr,
          `${cat.moyenne}`,
          `${cat.class_avg}`,
          comments.substring(0, 120) + (comments.length > 120 ? '...' : '')
        ]);
      }

      autoTable(doc, {
        startY: y,
        head: [['Axe', 'Notes', 'Moy.', 'Classe', 'Commentaires']],
        body,
        theme: 'grid',
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [141, 198, 63], textColor: 255, fontSize: 7 },
        columnStyles: {
          0: { cellWidth: 25, fontStyle: 'bold' },
          1: { cellWidth: 35 },
          2: { cellWidth: 12, halign: 'center' },
          3: { cellWidth: 12, halign: 'center' },
          4: { cellWidth: 'auto' }
        },
        margin: { left: 10, right: 10 }
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // AI Synthesis
    if (synthese) {
      if (y > 220) { doc.addPage(); y = 15; }
      doc.setFillColor(15, 23, 42);
      doc.rect(10, y, 190, 7, 'F');
      doc.setTextColor(255);
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Synthese IA', 14, y + 5);
      doc.setFont(undefined, 'normal');
      y += 10;

      if (synthese.synthese_globale) {
        doc.setTextColor(15, 23, 42);
        doc.setFontSize(8);
        const lines = doc.splitTextToSize(synthese.synthese_globale, 180);
        doc.text(lines, 14, y);
        y += lines.length * 4 + 5;
      }

      if (synthese.syntheses_supports) {
        for (const sc of synthese.syntheses_supports) {
          if (y > 270) { doc.addPage(); y = 15; }
          doc.setFontSize(8);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(30, 58, 95);
          doc.text(sc.support, 14, y);
          y += 4;
          doc.setFont(undefined, 'normal');
          doc.setTextColor(71, 85, 105);
          if (sc.synthese) {
            const slines = doc.splitTextToSize(sc.synthese, 180);
            doc.text(slines, 14, y);
            y += slines.length * 3.5 + 2;
          }
          if (sc.points_forts) {
            doc.setTextColor(22, 163, 74);
            const plines = doc.splitTextToSize(`+ ${sc.points_forts}`, 180);
            doc.text(plines, 14, y);
            y += plines.length * 3.5 + 2;
          }
          if (sc.axes_amelioration) {
            doc.setTextColor(234, 88, 12);
            const alines = doc.splitTextToSize(`- ${sc.axes_amelioration}`, 180);
            doc.text(alines, 14, y);
            y += alines.length * 3.5 + 4;
          }
        }
      }
    }

    doc.save(`Bulletin_${s.nom}_${s.prenom}.pdf`);
    toast.success('PDF telecharge');
  };

  const CompareArrow = ({ val, classVal }) => {
    if (!val || !classVal) return null;
    const diff = val - classVal;
    if (diff > 0.5) return <TrendingUp className="h-3 w-3 text-green-500 inline ml-1" />;
    if (diff < -0.5) return <TrendingDown className="h-3 w-3 text-red-500 inline ml-1" />;
    return <Minus className="h-3 w-3 text-slate-400 inline ml-1" />;
  };

  const noteColor = (n) => {
    if (n >= 7) return 'bg-green-100 text-green-700 border-green-200';
    if (n >= 4) return 'bg-orange-100 text-orange-700 border-orange-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  const catColor = (cat) => {
    if (cat === 'Aisance') return 'text-sky-600 bg-sky-50 border-sky-200';
    if (cat === 'Briefing') return 'text-amber-600 bg-amber-50 border-amber-200';
    if (cat === 'Encadrement') return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-[#8DC63F] border-t-transparent rounded-full" /></div>;
  if (!bulletin) return <div className="p-8 text-center text-slate-500">Bulletin non disponible</div>;

  const s = bulletin.stagiaire;

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="bulletin-notes-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to="/carnet-general" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm">
          <ArrowLeft className="h-4 w-4" /> Retour
        </Link>
        <div className="flex gap-2">
          <Button onClick={generateSynthese} disabled={syntheseLoading} variant={syntheseLoading ? "default" : "outline"} size="sm" className={syntheseLoading ? "bg-indigo-600 text-white animate-pulse" : ""} data-testid="generate-synthese-btn">
            {syntheseLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Brain className="h-4 w-4 mr-2" />}
            {syntheseLoading ? 'IA en cours... (~20s)' : synthese ? 'Regenerer IA' : 'Synthese IA'}
          </Button>
          <Button onClick={exportPDF} className="bg-[#0F172A] hover:bg-[#1e293b]" size="sm" data-testid="export-bulletin-pdf-btn">
            <Download className="h-4 w-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      {/* Student Card */}
      <div className="bg-gradient-to-r from-[#0F172A] to-[#1e293b] rounded-xl p-5 text-white">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 flex-shrink-0 flex items-center justify-center text-lg font-bold">
            {(s.prenom?.[0] || '')}{(s.nom?.[0] || '')}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold truncate">{s.prenom} {s.nom}</h1>
            <p className="text-white/60 text-xs">{s.centre_formation} {s.numero_candidat ? `| N${s.numero_candidat}` : ''} | {bulletin.total_notes} notes | {bulletin.supports.length} supports</p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-2xl font-bold text-[#8DC63F]">{bulletin.moyenne_generale}<span className="text-base text-white/50">/10</span></div>
            <p className="text-white/40 text-[10px]">Classe: {bulletin.classe_generale}/10</p>
          </div>
        </div>
      </div>

      {/* Supports */}
      {bulletin.supports.map(sup => {
        const IconComp = SUPPORT_ICONS[sup.support] || Ship;
        const synSup = synthese?.syntheses_supports?.find(sc => sc.support?.toUpperCase() === sup.support?.toUpperCase());
        
        return (
          <div key={sup.support} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`support-${sup.support}`}>
            {/* Support header */}
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a88] px-5 py-3 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <IconComp className="h-5 w-5 text-[#8DC63F]" />
                <h2 className="font-bold text-base">{sup.support}</h2>
                <span className="text-white/50 text-xs">{sup.total_notes} notes</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="font-bold text-[#8DC63F]">{sup.moyenne}/10</span>
                <span className="text-white/60 text-xs">
                  Classe: {sup.class_avg}/10
                  <CompareArrow val={sup.moyenne} classVal={sup.class_avg} />
                </span>
              </div>
            </div>

            {/* Categories inside support */}
            <div className="divide-y divide-slate-100">
              {sup.categories.map(cat => (
                <div key={cat.categorie} className="px-5 py-4">
                  {/* Category title bar */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${catColor(cat.categorie)}`}>
                        {cat.categorie}
                      </span>
                      <span className="text-slate-400 text-xs">{cat.notes.length} note{cat.notes.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className={`font-bold ${cat.moyenne >= 7 ? 'text-green-600' : cat.moyenne >= 4 ? 'text-orange-600' : 'text-red-600'}`}>
                        {cat.moyenne}/10
                      </span>
                      <span className="text-slate-400">
                        Classe: {cat.class_avg}
                        <CompareArrow val={cat.moyenne} classVal={cat.class_avg} />
                      </span>
                    </div>
                  </div>

                  {/* Notes list */}
                  <div className="space-y-2">
                    {cat.notes.map((n, i) => (
                      <div key={i} className="flex items-start gap-3 text-xs rounded-lg bg-slate-50/70 px-3 py-2 border border-slate-100">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded font-bold border ${noteColor(n.note)}`}>
                          {n.note}/10
                        </span>
                        <span className="text-slate-400 whitespace-nowrap pt-0.5">{n.date ? new Date(n.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '?'}</span>
                        <span className="text-slate-400 whitespace-nowrap pt-0.5">{n.formateur}</span>
                        {n.commentaire && <p className="text-slate-600 flex-1 leading-relaxed whitespace-pre-line">{n.commentaire}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* AI Synthesis for this support */}
            {synSup && (
              <div className="bg-indigo-50/50 border-t border-indigo-100 px-5 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <Brain className="h-4 w-4 text-indigo-500" />
                  <span className="text-xs font-semibold text-indigo-700">Synthese IA - {sup.support}</span>
                </div>
                <div className="text-xs text-slate-700 space-y-1.5">
                  <p>{synSup.synthese}</p>
                  {synSup.points_forts && <p className="text-green-700"><strong>Points forts :</strong> {synSup.points_forts}</p>}
                  {synSup.axes_amelioration && <p className="text-orange-700"><strong>A ameliorer :</strong> {synSup.axes_amelioration}</p>}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Global AI Synthesis */}
      {synthese?.synthese_globale && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-6" data-testid="synthese-globale">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="h-5 w-5 text-indigo-600" />
            <h2 className="font-semibold text-indigo-900">Synthese Globale</h2>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">{synthese.synthese_globale}</p>
        </div>
      )}
    </div>
  );
};

export default BulletinNotes;
